import type { SupabaseClient } from '@supabase/supabase-js'
import type { AppSupabaseClient } from '@/infrastructure/supabase/client'
import type { ProposalDeliveryRepository } from '../../application/ProposalDeliveryRepository'
import {
  clientPhoneLookupsSchema,
  phoneLookupSchema,
  proposalChannelsSchema,
  proposalSmsOptionsSchema,
  proposalSmsResultSchema,
} from '../../domain/proposalDelivery'

interface RpcResponse {
  data: unknown
  error: { message: string } | null
}

/** Erreur serveur : seul le code (`PHONE_NOT_ON_CLIENT`, …) est gardé, jamais le détail. */
export class ProposalDeliveryError extends Error {
  constructor(readonly code: string) {
    super(code)
    this.name = 'ProposalDeliveryError'
  }
}

/** Aucun repli sur des fixtures : un faux état d'envoi se lirait comme un fait. */
export function createSupabaseProposalDeliveryRepository(
  client: AppSupabaseClient,
): ProposalDeliveryRepository {
  const rpcClient = client as unknown as SupabaseClient

  async function call(name: string, args: Record<string, unknown>): Promise<unknown> {
    const response = (await rpcClient.rpc(name, args)) as unknown as RpcResponse
    if (response.error) {
      throw new ProposalDeliveryError(/[A-Z][A-Z0-9_]{3,}/.exec(response.error.message)?.[0] ?? 'UNEXPECTED')
    }
    return response.data
  }

  return {
    async smsOptions(contractId) {
      return proposalSmsOptionsSchema.parse(
        await call('contract_proposal_sms_options_v1', { p_contract_id: contractId }),
      )
    },
    async sendSms(command) {
      return proposalSmsResultSchema.parse(
        await call('send_contract_proposal_sms_v1', { p_command: command }),
      )
    },
    async channels(contractId) {
      return proposalChannelsSchema.parse(
        await call('contract_proposal_channels_v1', { p_contract_id: contractId }),
      )
    },
    async clientPhoneLookups(clientId) {
      return clientPhoneLookupsSchema.parse(await call('client_phone_lookups_v1', { p_client_id: clientId }))
    },
    async retryPhoneLookup(command) {
      return phoneLookupSchema.nullable().parse(await call('phone_lookup_retry_v1', { p_command: command }))
    },
  }
}
